import { router } from "@inertiajs/react";
import { useState } from "react";

import ConfirmDialog from "@/components/ConfirmDialog";
import type { Column, PagyMeta, RowAction } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import FilterBar, { type FilterField } from "@/components/FilterBar";
import LinkButton from "@/components/LinkButton";
import { type GenderedTextKey, useGenderedText } from "@/lib/genderedText";
import { usePermissions } from "@/lib/permissions";
import { houses } from "@/routes";
import type { House } from "@/types";

function exportUrl(path: string, filters: Record<string, string>): string {
  // Keep blank values: an explicit empty param (e.g. "status=") tells the
  // backend NOT to apply that filter's default.
  const qs = new URLSearchParams(Object.entries(filters)).toString();
  return qs ? `${path}?${qs}` : path;
}

const STATUS_LABELS: Record<string, string> = {
  active: "Activa",
  inactive: "Inactiva",
};

type Props = {
  data: House[];
  pagy: PagyMeta;
  filters: Record<string, string>;
};

function buildColumns(t: (key: GenderedTextKey) => string): Column<House>[] {
  return [
    {
      header: "Nombre",
      cell: (h) => h.name || "-",
    },
    {
      header: "Dirección",
      cell: (h) => h.address || "-",
    },
    {
      header: "Región",
      cell: (h) => (h.region as string | null) || "-",
    },
    {
      header: "Comuna",
      cell: (h) => (h.commune_name as string | null) || "-",
    },
    {
      header: t("administradoraCasa"),
      cell: (h) => (h.user_name as string | null) || "-",
    },
    {
      header: "Estado",
      cell: (h) => (h.status ? (STATUS_LABELS[h.status] ?? h.status) : "-"),
    },
  ];
}

const FILTER_FIELDS: FilterField[] = [
  {
    key: "name",
    label: "Nombre",
    type: "text",
    placeholder: "Buscar por nombre...",
  },
  {
    key: "status",
    label: "Estado",
    type: "select",
    options: [
      { value: "", label: "Todas" },
      { value: "active", label: "Activa" },
      { value: "inactive", label: "Inactiva" },
    ],
  },
];

export default function HousesIndex({ data: rows, pagy, filters }: Props) {
  const permissions = usePermissions();
  const t = useGenderedText();
  const columns = buildColumns(t);

  const [deleteTarget, setDeleteTarget] = useState<House | null>(null);

  const actions: RowAction<House>[] = [
    ...(permissions.canManageHouses
      ? ([
          {
            label: "Editar",
            href: (h) => houses.edit(h.id).url,
            variant: "primary",
          },
        ] satisfies RowAction<House>[])
      : []),
    ...(permissions.canManageHouses
      ? ([
          {
            label: "Eliminar",
            onClick: (h) => setDeleteTarget(h),
            variant: "danger",
          },
        ] satisfies RowAction<House>[])
      : []),
  ];

  function navigate(
    params: Record<string, string | number>,
    opts?: { replace?: boolean },
  ) {
    router.get(houses.index().url, params, {
      preserveScroll: true,
      replace: opts?.replace ?? false,
    });
  }

  return (
    <>
      <div className="w-full py-10">
        <div className="px-6 sm:px-8 lg:px-12">
          <div className="mb-6 flex items-center justify-between gap-4">
            <h1 className="font-sans text-3xl font-semibold text-ink">Casas</h1>

            {permissions.canManageAdminOnlyRecords && (
              <LinkButton
                href={houses.new().url}
                variant="primary"
                className="px-4 py-1.5 text-sm"
              >
                Nuevo
              </LinkButton>
            )}
          </div>

          <FilterBar
            fields={FILTER_FIELDS}
            values={filters}
            onChange={(newFilters) =>
              navigate({ ...newFilters, page: 1 }, { replace: true })
            }
          />
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          keyExtractor={(h) => h.id}
          actions={actions}
          utilities={
            <a
              href={exportUrl("/houses/export_excel", filters)}
              className="font-sans text-sm font-light text-ink underline underline-offset-2 hover:text-steel"
            >
              Exportar Excel
            </a>
          }
          pagy={pagy}
          onPageChange={(page) => navigate({ ...filters, page })}
          emptyMessage="No hay casas registradas."
        />
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Eliminar casa"
        message={`Se eliminará la casa ${deleteTarget?.name ?? ""}. Sus cursos quedarán sin casa asignada.`}
        confirmLabel="Eliminar"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) {
            router.delete(houses.destroy(deleteTarget.id).url, {
              preserveScroll: true,
            });
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
